<template>
  <div class="prefilter-builder">
    <!-- Match mode selector (shown when 2+ filters) -->
    <div v-if="filters.length > 1" class="pfb-match-mode">
      <span class="pfb-match-label">Match</span>
      <button
        class="pfb-match-btn"
        :class="{ active: matchMode === 'all' }"
        @click="$emit('update:match-mode', 'all')"
      >all rules</button>
      <button
        class="pfb-match-btn"
        :class="{ active: matchMode === 'any' }"
        @click="$emit('update:match-mode', 'any')"
      >any rule</button>
    </div>

    <!-- Filter list -->
    <div v-if="filters.length" class="pfb-filter-list">
      <template v-for="(filter, idx) in filters" :key="idx">
        <div v-if="idx > 0" class="pfb-connector">
          <span class="pfb-connector-line"></span>
          <span class="pfb-connector-label">{{ matchMode === 'all' ? 'AND' : 'OR' }}</span>
          <span class="pfb-connector-line"></span>
        </div>
        <div class="pfb-filter-item">
          <button type="button" class="pfb-filter-open" @click="editFilter(idx)">
          <span class="pfb-source-badge" :class="'pfb-source-' + filter.source">
            {{ getSourceBadge(filter) }}
          </span>
          <span class="pfb-filter-body">
            <span class="pfb-filter-field">{{ getFieldLabel(filter) }}</span>
            <span
              v-if="filter.relQuantifier && (filter.source === 'forward' || filter.source === 'reverse')"
              class="pfb-filter-quant"
              :class="'pfb-quant-' + filter.relQuantifier"
            >{{ filter.relQuantifier }}</span>
            <span class="pfb-filter-op">{{ getOperatorLabel(filter.operator) }}</span>
            <span v-if="!isNoValueOp(filter.operator)" class="pfb-filter-val" :class="{ 'pfb-field-ref': filter.valueType === 'field' }">
              {{ formatDisplayValue(filter) }}
            </span>
          </span>
          </button>
          <button type="button" class="pfb-filter-remove" @click.stop="removeFilter(idx)" aria-label="Remove filter">&times;</button>
        </div>
      </template>
    </div>

    <!-- Empty state -->
    <div v-if="!filters.length && !isAdding" class="pfb-empty">
      {{ emptyLabel }}
    </div>

    <!-- Add / Edit form -->
    <div v-if="isAdding" class="pfb-add-form">
      <div class="pfb-add-title">{{ editingIndex !== null ? 'Edit' : 'New' }} filter rule</div>

      <div class="pfb-form-row">
        <label class="pfb-form-label">Field</label>
        <select v-model="newFieldKey" @change="onFieldChange" class="pfb-select">
          <option value="" disabled>Select a field...</option>
          <optgroup v-for="group in fieldGroups" :key="group.key" :label="group.label">
            <option v-for="f in group.fields" :key="f.key" :value="f.key">
              {{ f.displayName }}
            </option>
          </optgroup>
        </select>
      </div>

      <div v-if="newFieldKey && selectedFieldIsRelationship" class="pfb-form-row">
        <label class="pfb-form-label">Related records</label>
        <div class="pfb-quant-toggle">
          <button class="pfb-qt-btn" :class="{ active: newRelQuantifier === 'any' }" @click="newRelQuantifier = 'any'">
            <span class="pfb-qt-icon">&#8707;</span> any match
          </button>
          <button class="pfb-qt-btn" :class="{ active: newRelQuantifier === 'all' }" @click="newRelQuantifier = 'all'">
            <span class="pfb-qt-icon">&#8704;</span> all match
          </button>
        </div>
        <div class="pfb-qt-hint">
          {{ newRelQuantifier === 'any'
            ? 'Show record if at least one related record meets the condition'
            : 'Show record only if every related record meets the condition' }}
        </div>
      </div>

      <div v-if="newFieldKey" class="pfb-form-row">
        <label class="pfb-form-label">Operator</label>
        <select v-model="newOperator" class="pfb-select">
          <option value="" disabled>Select operator...</option>
          <option v-for="op in operatorsForSelectedField" :key="op.key" :value="op.key">
            {{ op.label }}
          </option>
        </select>
      </div>

      <template v-if="newOperator && !isNoValueOp(newOperator)">
        <!-- Relationship fields: record picker -->
        <template v-if="selectedFieldDataType === 'relationship' && isRelValueOp(newOperator)">
          <div class="pfb-form-row">
            <label class="pfb-form-label">Records</label>
            <div v-if="relSelectedRecords.length" class="pfb-rel-tags">
              <span v-for="rec in relSelectedRecords" :key="rec.id" class="pfb-rel-tag">
                {{ rec.displayValue }}
                <button class="pfb-rel-tag-remove" @click="removeRelRecord(rec.id)">&times;</button>
              </span>
            </div>
            <div class="pfb-rel-search-wrap">
              <input
                type="text"
                v-model="relSearchQuery"
                class="pfb-input"
                placeholder="Search records..."
                @input="onRelSearchInput"
              />
              <div v-if="relSearchLoading" class="pfb-rel-loading">searching...</div>
            </div>
            <div v-if="relSearchResults.length" class="pfb-rel-results">
              <div
                v-for="rec in relSearchResults"
                :key="rec.id"
                class="pfb-rel-result-item"
                @click="selectRelRecord(rec)"
              >{{ rec.displayValue || '(no value)' }}</div>
            </div>
          </div>
        </template>
        <!-- Number fields: toggle between literal value and field reference -->
        <template v-else-if="selectedFieldDataType === 'number'">
          <div class="pfb-form-row">
            <label class="pfb-form-label">Compare against</label>
            <div class="pfb-value-toggle">
              <button class="pfb-vt-btn" :class="{ active: newValueType === 'literal' }" @click="setValueType('literal')">a value</button>
              <button class="pfb-vt-btn" :class="{ active: newValueType === 'field' }" @click="setValueType('field')">a field</button>
            </div>
          </div>
          <div class="pfb-form-row">
            <label class="pfb-form-label">{{ newValueType === 'field' ? 'Field' : 'Value' }}</label>
            <input
              v-if="newValueType === 'literal'"
              type="number"
              v-model="newValue"
              class="pfb-input"
              placeholder="Enter a number..."
              @keyup.enter="confirmAdd"
            />
            <select v-else v-model="newValueFieldKey" class="pfb-select">
              <option value="" disabled>Select a numeric field...</option>
              <optgroup v-for="group in numericFieldGroups" :key="group.key" :label="group.label">
                <option v-for="f in group.fields" :key="f.key" :value="f.key">
                  {{ f.displayName }}
                </option>
              </optgroup>
            </select>
          </div>
        </template>
        <!-- DateTime -->
        <div v-else-if="selectedFieldDataType === 'datetime'" class="pfb-form-row">
          <label class="pfb-form-label">Value</label>
          <input type="datetime-local" v-model="newValue" class="pfb-input" />
        </div>
        <!-- String (default) -->
        <div v-else class="pfb-form-row">
          <label class="pfb-form-label">Value</label>
          <input type="text" v-model="newValue" class="pfb-input" placeholder="Enter a value..." @keyup.enter="confirmAdd" />
        </div>
      </template>

      <div class="pfb-form-actions">
        <button class="pfb-btn pfb-btn-cancel" @click="cancelAdd">Cancel</button>
        <button class="pfb-btn pfb-btn-confirm" :disabled="!isFormValid" @click="confirmAdd">
          {{ editingIndex !== null ? 'Save' : 'Add Filter' }}
        </button>
      </div>
    </div>

    <!-- Add button -->
    <button v-if="!isAdding" class="pfb-add-btn" @click="startAdd">+ add filter rule</button>
  </div>
</template>

<script>
const TYPE_MAP = {
  text: 'string',
  password: 'string',
  number: 'number',
  dateTime: 'datetime',
  formula: 'string',
  relationship: 'relationship',
  address: 'string',
  duration: 'number',
  sequence: 'number',
  signature: 'signature'
}

const OPERATORS = {
  string: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'does not equal' },
    { key: 'contains', label: 'contains' },
    { key: 'begins', label: 'starts with' },
    { key: 'ends', label: 'ends with' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  number: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'does not equal' },
    { key: '<', label: 'less than' },
    { key: '<=', label: 'less than or equal to' },
    { key: '>', label: 'greater than' },
    { key: '>=', label: 'greater than or equal to' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  datetime: [
    { key: '=', label: 'equals' },
    { key: '<', label: 'is before' },
    { key: '<=', label: 'is on or before' },
    { key: '>', label: 'is after' },
    { key: '>=', label: 'is on or after' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  relationship: [
    { key: 'is', label: 'is' },
    { key: 'isNot', label: 'is not' },
    { key: 'includes', label: 'includes' },
    { key: 'doesNotInclude', label: 'does not include' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  signature: [
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ]
}

const REL_VALUE_OPS = new Set(['is', 'isNot', 'includes', 'doesNotInclude'])

function mapColumnType (columnType) {
  return TYPE_MAP[columnType] || null
}

function buildFieldsFromColumns (columns, includeBuiltins, includeRelationships) {
  const fields = []

  if (includeBuiltins) {
    fields.push({ field: 'id', displayName: 'ID', dataType: 'number' })
  }

  if (columns) {
    for (const col of columns) {
      const dt = mapColumnType(col.type)
      if (!dt) continue
      if (dt === 'relationship' && !includeRelationships) continue
      const entry = {
        field: col.uniqueTitle,
        displayName: col.title || col.uniqueTitle,
        dataType: dt
      }
      if (dt === 'relationship') {
        entry.relatedSpreadSheet = col.relatedSpreadSheet
        entry.relationshipType = col.relationshipType
      }
      fields.push(entry)
    }
  }

  if (includeBuiltins) {
    fields.push({ field: 'createdAt', displayName: 'Created At', dataType: 'datetime' })
    fields.push({ field: 'updatedAt', displayName: 'Updated At', dataType: 'datetime' })
  }

  return fields
}

export default {
  name: 'PreFilterBuilder',
  props: {
    filters: {
      type: Array,
      default: () => []
    },
    matchMode: {
      type: String,
      default: 'all'
    },
    emptyLabel: {
      type: String,
      default: 'No prefilter rules configured'
    },
    spreadsheet: {
      type: Object,
      default: () => ({})
    },
    allSpreadsheets: {
      type: Array,
      default: () => []
    },
    applicationUniqueTitle: {
      type: String,
      default: ''
    }
  },
  emits: ['update:filters', 'update:match-mode'],
  data () {
    return {
      isAdding: false,
      editingIndex: null,
      newFieldKey: '',
      newOperator: '',
      newValue: '',
      newValueType: 'literal',
      newValueFieldKey: '',
      newRelQuantifier: 'any',
      relSearchQuery: '',
      relSearchResults: [],
      relSearchLoading: false,
      relSelectedRecords: [],
      relSearchTimeout: null
    }
  },
  computed: {
    currentColumns () {
      return (this.spreadsheet && this.spreadsheet.columns) || []
    },

    relationshipColumns () {
      return this.currentColumns.filter(c => c.type === 'relationship')
    },

    reverseRelationships () {
      const ssTitle = this.spreadsheet && this.spreadsheet.uniqueTitle
      if (!ssTitle) return {}
      const bySheet = {}
      for (const ss of this.allSpreadsheets) {
        if (ss.uniqueTitle === ssTitle) continue
        if (!ss.columns) continue
        for (const col of ss.columns) {
          if (col.type === 'relationship' && col.relatedSpreadSheet === ssTitle) {
            if (!bySheet[ss.uniqueTitle]) {
              bySheet[ss.uniqueTitle] = { spreadsheet: ss, columns: [] }
            }
            bySheet[ss.uniqueTitle].columns.push(col)
          }
        }
      }
      return bySheet
    },

    fieldGroups () {
      const groups = []

      const directFields = buildFieldsFromColumns(this.currentColumns, true, true)
      groups.push({
        key: 'direct',
        label: this.spreadsheet.title || 'This Spreadsheet',
        fields: directFields.map(f => ({
          ...f,
          key: `direct::${f.field}`,
          source: 'direct',
          relationshipColumn: null,
          relatedSpreadsheet: null,
          sourceSpreadsheet: null,
          sourceRelationshipColumn: null
        }))
      })

      for (const relCol of this.relationshipColumns) {
        const relSS = this.allSpreadsheets.find(s => s.uniqueTitle === relCol.relatedSpreadSheet)
        if (!relSS) continue

        const relFields = buildFieldsFromColumns(relSS.columns, true, false)
        groups.push({
          key: `forward::${relCol.uniqueTitle}`,
          label: `\u2192 ${relSS.title || relSS.uniqueTitle} (via ${relCol.title || relCol.uniqueTitle})`,
          fields: relFields.map(f => ({
            ...f,
            key: `forward::${relCol.uniqueTitle}::${f.field}`,
            source: 'forward',
            relationshipColumn: relCol.uniqueTitle,
            relatedSpreadsheet: relSS.uniqueTitle,
            sourceSpreadsheet: null,
            sourceRelationshipColumn: null
          }))
        })
      }

      for (const [ssName, data] of Object.entries(this.reverseRelationships)) {
        for (const relCol of data.columns) {
          const revFields = buildFieldsFromColumns(data.spreadsheet.columns, true, false)
          groups.push({
            key: `reverse::${ssName}::${relCol.uniqueTitle}`,
            label: `\u2190 ${data.spreadsheet.title || ssName} (their ${relCol.title || relCol.uniqueTitle} \u2192 here)`,
            fields: revFields.map(f => ({
              ...f,
              key: `reverse::${ssName}::${relCol.uniqueTitle}::${f.field}`,
              source: 'reverse',
              relationshipColumn: null,
              relatedSpreadsheet: null,
              sourceSpreadsheet: ssName,
              sourceRelationshipColumn: relCol.uniqueTitle
            }))
          })
        }
      }

      return groups
    },

    allFieldOptions () {
      const flat = []
      for (const group of this.fieldGroups) {
        for (const f of group.fields) {
          flat.push(f)
        }
      }
      return flat
    },

    selectedField () {
      if (!this.newFieldKey) return null
      return this.allFieldOptions.find(f => f.key === this.newFieldKey) || null
    },

    selectedFieldDataType () {
      return this.selectedField ? this.selectedField.dataType : null
    },

    selectedFieldIsRelationship () {
      if (!this.selectedField) return false
      return this.selectedField.source === 'forward' || this.selectedField.source === 'reverse'
    },

    operatorsForSelectedField () {
      if (!this.selectedFieldDataType) return []
      return OPERATORS[this.selectedFieldDataType] || OPERATORS.string
    },

    numericFieldGroups () {
      return this.fieldGroups
        .map(group => ({
          ...group,
          fields: group.fields.filter(f => f.dataType === 'number')
        }))
        .filter(group => group.fields.length > 0)
    },

    selectedValueField () {
      if (!this.newValueFieldKey) return null
      return this.allFieldOptions.find(f => f.key === this.newValueFieldKey) || null
    },

    isFormValid () {
      if (!this.newFieldKey || !this.newOperator) return false
      if (this.isNoValueOp(this.newOperator)) return true
      if (this.selectedFieldDataType === 'relationship') {
        return this.relSelectedRecords.length > 0
      }
      if (this.selectedFieldDataType === 'number' && this.newValueType === 'field') {
        return !!this.newValueFieldKey
      }
      if (!this.newValue && this.newValue !== 0) return false
      return true
    }
  },
  methods: {
    getSourceBadge (filter) {
      if (filter.source === 'forward') return '\u2192'
      if (filter.source === 'reverse') return '\u2190'
      return '\u25CF'
    },

    getFieldLabel (filter) {
      if (filter.source === 'forward') {
        const relSS = this.allSpreadsheets.find(s => s.uniqueTitle === filter.relatedSpreadsheet)
        const ssLabel = relSS ? relSS.title : filter.relatedSpreadsheet
        return `${ssLabel} \u203A ${filter.field}`
      }
      if (filter.source === 'reverse') {
        const ss = this.allSpreadsheets.find(s => s.uniqueTitle === filter.sourceSpreadsheet)
        const ssLabel = ss ? ss.title : filter.sourceSpreadsheet
        return `${ssLabel} \u203A ${filter.field}`
      }

      const col = this.currentColumns.find(c => c.uniqueTitle === filter.field)
      if (col) return col.title || col.uniqueTitle
      if (filter.field === 'id') return 'ID'
      if (filter.field === 'createdAt') return 'Created At'
      if (filter.field === 'updatedAt') return 'Updated At'
      return filter.field
    },

    getOperatorLabel (operatorKey) {
      for (const typeOps of Object.values(OPERATORS)) {
        const found = typeOps.find(o => o.key === operatorKey)
        if (found) return found.label
      }
      return operatorKey
    },

    isNoValueOp (operatorKey) {
      return operatorKey === 'empty' || operatorKey === 'notEmpty'
    },

    formatDisplayValue (filter) {
      if (filter.valueType === 'field' && filter.valueFieldRef) {
        return this.getFieldLabel(filter.valueFieldRef)
      }
      if (filter.dataType === 'relationship' && Array.isArray(filter.value)) {
        return filter.value.map(r => r.displayValue || r.id).join(', ')
      }
      if (filter.dataType === 'number') return filter.value
      if (filter.dataType === 'datetime' && filter.value) {
        try {
          const d = new Date(filter.value)
          if (!isNaN(d.getTime())) return d.toLocaleString()
        } catch { /* fall through */ }
      }
      return `"${filter.value}"`
    },

    onFieldChange () {
      this.newOperator = ''
      this.newValue = ''
      this.newValueType = 'literal'
      this.newValueFieldKey = ''
      this.newRelQuantifier = 'any'
      this.relSearchQuery = ''
      this.relSearchResults = []
      this.relSelectedRecords = []
    },

    setValueType (type) {
      this.newValueType = type
      if (type === 'literal') {
        this.newValueFieldKey = ''
      } else {
        this.newValue = ''
      }
    },

    startAdd () {
      this.isAdding = true
      this.editingIndex = null
      this.newFieldKey = ''
      this.newOperator = ''
      this.newValue = ''
      this.newValueType = 'literal'
      this.newValueFieldKey = ''
      this.newRelQuantifier = 'any'
      this.relSearchQuery = ''
      this.relSearchResults = []
      this.relSelectedRecords = []
    },

    cancelAdd () {
      this.isAdding = false
      this.editingIndex = null
    },

    confirmAdd () {
      if (!this.isFormValid) return

      const field = this.selectedField
      const isFieldRef = this.selectedFieldDataType === 'number' && this.newValueType === 'field'
      const isRelDataType = this.selectedFieldDataType === 'relationship'

      const isRel = field.source === 'forward' || field.source === 'reverse'

      let filterValue
      if (this.isNoValueOp(this.newOperator)) {
        filterValue = ''
      } else if (isRelDataType) {
        filterValue = [...this.relSelectedRecords]
      } else if (isFieldRef) {
        filterValue = ''
      } else {
        filterValue = this.newValue
      }

      const newFilter = {
        field: field.field,
        source: field.source,
        relationshipColumn: field.relationshipColumn || null,
        relatedSpreadsheet: field.relatedSpreadsheet || null,
        relatedSpreadSheet: field.relatedSpreadSheet || null,
        sourceSpreadsheet: field.sourceSpreadsheet || null,
        sourceRelationshipColumn: field.sourceRelationshipColumn || null,
        dataType: field.dataType,
        relQuantifier: isRel ? this.newRelQuantifier : null,
        operator: this.newOperator,
        valueType: isFieldRef ? 'field' : 'literal',
        value: filterValue,
        valueFieldRef: null
      }

      if (isFieldRef && this.selectedValueField) {
        const vf = this.selectedValueField
        newFilter.valueFieldRef = {
          field: vf.field,
          source: vf.source,
          dataType: vf.dataType,
          relationshipColumn: vf.relationshipColumn || null,
          relatedSpreadsheet: vf.relatedSpreadsheet || null,
          sourceSpreadsheet: vf.sourceSpreadsheet || null,
          sourceRelationshipColumn: vf.sourceRelationshipColumn || null
        }
      }

      const updated = [...this.filters]
      if (this.editingIndex !== null) {
        updated[this.editingIndex] = newFilter
      } else {
        updated.push(newFilter)
      }

      this.$emit('update:filters', updated)
      this.isAdding = false
      this.editingIndex = null
    },

    removeFilter (idx) {
      const updated = [...this.filters]
      updated.splice(idx, 1)
      this.$emit('update:filters', updated)
    },

    editFilter (idx) {
      const filter = this.filters[idx]
      this.editingIndex = idx
      this.isAdding = true

      if (filter.source === 'forward') {
        this.newFieldKey = `forward::${filter.relationshipColumn}::${filter.field}`
      } else if (filter.source === 'reverse') {
        this.newFieldKey = `reverse::${filter.sourceSpreadsheet}::${filter.sourceRelationshipColumn}::${filter.field}`
      } else {
        this.newFieldKey = `direct::${filter.field}`
      }

      this.newOperator = filter.operator
      this.newRelQuantifier = filter.relQuantifier || 'any'

      if (filter.dataType === 'relationship' && Array.isArray(filter.value)) {
        this.relSelectedRecords = [...filter.value]
        this.newValue = ''
      } else {
        this.newValue = filter.value || ''
        this.relSelectedRecords = []
      }

      this.relSearchQuery = ''
      this.relSearchResults = []

      if (filter.valueType === 'field' && filter.valueFieldRef) {
        this.newValueType = 'field'
        const ref = filter.valueFieldRef
        if (ref.source === 'forward') {
          this.newValueFieldKey = `forward::${ref.relationshipColumn}::${ref.field}`
        } else if (ref.source === 'reverse') {
          this.newValueFieldKey = `reverse::${ref.sourceSpreadsheet}::${ref.sourceRelationshipColumn}::${ref.field}`
        } else {
          this.newValueFieldKey = `direct::${ref.field}`
        }
      } else {
        this.newValueType = 'literal'
        this.newValueFieldKey = ''
      }
    },

    isRelValueOp (operatorKey) {
      return REL_VALUE_OPS.has(operatorKey)
    },

    getRelatedSpreadSheetForField () {
      if (!this.selectedField) return null
      return this.selectedField.relatedSpreadSheet || null
    },

    onRelSearchInput () {
      if (this.relSearchTimeout) clearTimeout(this.relSearchTimeout)
      this.relSearchTimeout = setTimeout(() => this.searchRelRecords(), 250)
    },

    async searchRelRecords () {
      const relSS = this.getRelatedSpreadSheetForField()
      if (!relSS) return

      this.relSearchLoading = true
      try {
        const headers = {}
        if (this.applicationUniqueTitle) {
          headers['application-unique-title'] = this.applicationUniqueTitle
        }
        const field = this.selectedField
        const owner = this.spreadsheet && this.spreadsheet.uniqueTitle
        const useOptions = field && field.dataType === 'relationship' && owner && field.field
        const response = await fetch(
          useOptions
            ? `${VUE_APP_PROXY_SERVER_URL}/data/${owner}/relationships/${field.field}/options?forLink=0&q=${encodeURIComponent(this.relSearchQuery)}`
            : `${VUE_APP_PROXY_SERVER_URL}/data/${relSS}/search?q=${encodeURIComponent(this.relSearchQuery)}`,
          { headers }
        )
        const data = await response.json()
        const rows = Array.isArray(data) ? data : (data.options || [])
        const selectedIds = new Set(this.relSelectedRecords.map(r => r.id))
        this.relSearchResults = rows
          .map(row => ({ id: row.id, displayValue: row.displayValue || row.label || String(row.id) }))
          .filter(r => !selectedIds.has(r.id))
      } catch (err) {
        console.error('relationship search error:', err)
        this.relSearchResults = []
      } finally {
        this.relSearchLoading = false
      }
    },

    selectRelRecord (rec) {
      if (!this.relSelectedRecords.find(r => r.id === rec.id)) {
        this.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
      }
      this.relSearchResults = this.relSearchResults.filter(r => r.id !== rec.id)
      this.relSearchQuery = ''
    },

    removeRelRecord (id) {
      this.relSelectedRecords = this.relSelectedRecords.filter(r => r.id !== id)
    }
  }
}
</script>

<style scoped>
.prefilter-builder {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #141412;
  padding: 12px;
  margin-top: 8px;
}

/* Match mode */
.pfb-match-mode {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #2a2a27;
}
.pfb-match-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #7a7a78;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-right: 4px;
}
.pfb-match-btn {
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  color: #7a7a78;
  font-size: 0.78rem;
  padding: 3px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.pfb-match-btn:hover {
  border-color: #474842;
  color: #b0b0ae;
}
.pfb-match-btn.active {
  background: #1e2e12;
  border-color: #9ad12b;
  color: #9ad12b;
  font-weight: 600;
}

/* Filter list */
.pfb-filter-list {
  margin-bottom: 10px;
}

/* Connector (AND/OR) */
.pfb-connector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0;
  padding: 0 4px;
}
.pfb-connector-line {
  flex: 1;
  height: 1px;
  background: #2e2e2b;
}
.pfb-connector-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  flex-shrink: 0;
}

/* Filter item */
.pfb-filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  border-radius: 5px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.12s;
}
.pfb-filter-item:hover {
  border-color: #474842;
  background: #1e1e1b;
}
.pfb-filter-open {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}

/* Source badge */
.pfb-source-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}
.pfb-source-direct {
  background: #1e2e12;
  color: #9ad12b;
}
.pfb-source-forward {
  background: #2e2512;
  color: #d0a05b;
}
.pfb-source-reverse {
  background: #251e30;
  color: #a05bd0;
}

/* Filter body */
.pfb-filter-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}
.pfb-filter-field {
  color: #e8e8e6;
  font-size: 0.82rem;
  font-weight: 500;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  white-space: nowrap;
}
.pfb-filter-op {
  color: #6a6a67;
  font-size: 0.78rem;
  white-space: nowrap;
}
.pfb-filter-val {
  color: #5ba0d0;
  font-size: 0.82rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
.pfb-filter-val.pfb-field-ref {
  color: #d0a05b;
  font-style: italic;
}

/* Quantifier badge in filter display */
.pfb-filter-quant {
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
  height: 17px;
  border-radius: 3px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex-shrink: 0;
}
.pfb-quant-any {
  background: #1a2a3a;
  color: #5ba0d0;
}
.pfb-quant-all {
  background: #2a1a3a;
  color: #a05bd0;
}

/* Remove button */
.pfb-filter-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  border: 1px solid transparent;
  color: #a8a8a4;
  font-size: 16px;
  line-height: 1;
  border-radius: 3px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  padding: 0;
}
.pfb-filter-remove:hover {
  background: #f82471;
  border-color: #f82471;
  color: #fff;
}

/* Empty state */
.pfb-empty {
  color: #4a4a47;
  font-size: 0.8rem;
  font-style: italic;
  padding: 8px 4px;
}

/* Add form */
.pfb-add-form {
  background: #1a1a17;
  border: 1px solid #3a3a37;
  border-radius: 5px;
  padding: 12px;
  margin-top: 8px;
}
.pfb-add-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #9ad12b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
}

.pfb-form-row {
  margin-bottom: 10px;
}
.pfb-form-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #7a7a78;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
}

.pfb-select,
.pfb-input {
  width: 100%;
  padding: 6px 10px;
  background: #141412;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  color: #e8e8e6;
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  -webkit-appearance: none;
}
.pfb-select:focus,
.pfb-input:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 2px rgba(154, 209, 43, 0.15);
}
.pfb-select option,
.pfb-select optgroup {
  background: #1a1a17;
  color: #e8e8e6;
}
.pfb-input::placeholder {
  color: #4a4a47;
}

/* Quantifier toggle */
.pfb-quant-toggle {
  display: flex;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  overflow: hidden;
}
.pfb-qt-btn {
  flex: 1;
  padding: 5px 12px;
  background: #141412;
  border: none;
  border-right: 1px solid #3a3a37;
  color: #7a7a78;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.pfb-qt-btn:last-child {
  border-right: none;
}
.pfb-qt-btn:hover {
  color: #b0b0ae;
  background: #1e1e1b;
}
.pfb-qt-btn.active {
  font-weight: 600;
}
.pfb-qt-btn.active:first-child {
  background: #142030;
  color: #5ba0d0;
}
.pfb-qt-btn.active:last-child {
  background: #201430;
  color: #a05bd0;
}
.pfb-qt-icon {
  font-size: 0.9rem;
  font-weight: 700;
}
.pfb-qt-hint {
  font-size: 0.7rem;
  color: #4a4a47;
  font-style: italic;
  margin-top: 4px;
  line-height: 1.3;
}

/* Value type toggle */
.pfb-value-toggle {
  display: flex;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  overflow: hidden;
}
.pfb-vt-btn {
  flex: 1;
  padding: 4px 12px;
  background: #141412;
  border: none;
  border-right: 1px solid #3a3a37;
  color: #7a7a78;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s;
}
.pfb-vt-btn:last-child {
  border-right: none;
}
.pfb-vt-btn:hover {
  color: #b0b0ae;
  background: #1e1e1b;
}
.pfb-vt-btn.active {
  background: #1e2e12;
  color: #9ad12b;
  font-weight: 600;
}

/* Form actions */
.pfb-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.pfb-btn {
  padding: 5px 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.pfb-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pfb-btn-cancel {
  background: transparent;
  border-color: #3a3a37;
  color: #7a7a78;
}
.pfb-btn-cancel:hover {
  border-color: #474842;
  color: #b0b0ae;
}
.pfb-btn-confirm {
  background: #9ad12b;
  border-color: #9ad12b;
  color: #141412;
}
.pfb-btn-confirm:hover:not(:disabled) {
  background: #b0e040;
  border-color: #b0e040;
}

/* Relationship record picker */
.pfb-rel-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}
.pfb-rel-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #1e2e12;
  border: 1px solid #3a5a1e;
  border-radius: 3px;
  color: #9ad12b;
  font-size: 0.78rem;
}
.pfb-rel-tag-remove {
  background: none;
  border: none;
  color: #7a7a78;
  font-size: 14px;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
}
.pfb-rel-tag-remove:hover {
  color: #f82471;
}
.pfb-rel-search-wrap {
  position: relative;
}
.pfb-rel-loading {
  font-size: 0.7rem;
  color: #a8a8a4;
  font-style: italic;
  margin-top: 3px;
}
.pfb-rel-results {
  border: 1px solid #3a3a37;
  border-radius: 4px;
  background: #141412;
  max-height: 160px;
  overflow-y: auto;
  margin-top: 4px;
}
.pfb-rel-result-item {
  padding: 5px 10px;
  font-size: 0.82rem;
  color: #e8e8e6;
  cursor: pointer;
  transition: background 0.1s;
}
.pfb-rel-result-item:hover {
  background: #1e2e12;
  color: #9ad12b;
}

/* Add button */
.pfb-add-btn {
  display: inline-block;
  margin-top: 6px;
  padding: 5px 14px;
  background: transparent;
  border: 1px dashed #3a3a37;
  border-radius: 4px;
  color: #9ad12b;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.pfb-add-btn:hover {
  border-color: #9ad12b;
  background: #1a2212;
  color: #b0e040;
}
</style>
